import { t } from '@lingui/macro';

/**
 * Account-identifier field mapping (SEB-1664).
 *
 * Some Volt banks require the payer to supply account identifiers on payment
 * initiation. The BE advertises the required keys per bank as `inputRequired`
 * (SNAKE_CASE, e.g. `["PSU_ID"]`) and forwards the collected values back to
 * Volt under `accountIdentifiers`. This module maps each identifier key to its
 * presentation (label, placeholder, help text) and its format validation, per
 * the mapping table on the ticket.
 *
 * Copy goes through Lingui `t`, so this must be *called at render time* —
 * never memoised at module scope — for the active locale to apply. Some
 * labels interpolate the selected bank's name, so callers pass a `BankContext`
 * (name + country). An unknown key falls back to using the raw key as the
 * label with a required-non-empty check, so a new identifier type Volt
 * introduces is still collected and forwarded without a code change.
 */
export interface BankContext {
  name: string;
  country: string;
}

export interface AccountIdentifierField {
  label: string;
  hint?: string;
  placeholder?: string;
  /** Actionable message shown when `validate` fails — tells the user what to enter. */
  error: string;
  validate: (value: string) => boolean;
}

const sanitizeIban = (value: string): string => value.replace(/\s/g, '').toUpperCase();

/** Standard ISO 7064 MOD-97-10 IBAN check. */
const ibanMod97 = (iban: string): boolean => {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    // A–Z → 10–35, digits stay as-is; fold each resulting digit into the
    // running remainder to avoid big-integer maths.
    const expanded = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of expanded) {
      remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
    }
  }
  return remainder === 1;
};

const isValidIban = (raw: string, country?: string): boolean => {
  const iban = sanitizeIban(raw);
  // Country code + 2 check digits + BBAN, total 5–34 chars.
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(iban)) return false;
  // Must start with the selected bank's country code when we know it.
  if (country && iban.slice(0, 2) !== country.toUpperCase()) return false;
  return ibanMod97(iban);
};

const isRequiredAlphanumeric = (value: string): boolean => /^[A-Za-z0-9]+$/.test(value.trim());

export const getAccountIdentifierField = (
  key: string,
  bank?: BankContext,
): AccountIdentifierField => {
  const bankName = bank?.name ?? '';
  const country = (bank?.country ?? '').toUpperCase();
  switch (key) {
    case 'PSU_ID':
      return {
        label: t`${bankName} ID`,
        placeholder: t`Your user ID, personal ID, or access code`,
        hint: t`The code or alias you regularly use to log into your online banking.`,
        error: t`Enter a value using only letters and numbers.`,
        validate: isRequiredAlphanumeric,
      };
    case 'IBAN':
      return {
        label: t`Account number (IBAN)`,
        placeholder: t`GB00 XXXX 0000 0000 0000 00`,
        hint: t`The international identifier of your bank account. Starts with two letters.`,
        error: country
          ? t`Enter a valid IBAN that starts with ${country}.`
          : t`Enter a valid IBAN. It must start with two letters.`,
        validate: (value) => isValidIban(value, bank?.country),
      };
    case 'PSU_CORPORATE_ID':
      return {
        label: t`Corporate ID`,
        placeholder: t`Company code or corporate tax ID`,
        hint: t`Only required if you are operating with a business or corporate bank account.`,
        error: t`Enter a value using only letters and numbers.`,
        validate: isRequiredAlphanumeric,
      };
    case 'SORT_CODE':
      return {
        label: t`Branch code (Sort Code)`,
        placeholder: t`00-00-00`,
        hint: t`The 6-digit code that identifies your bank branch in the UK.`,
        error: t`Enter a 6-digit sort code, e.g. 12-34-56.`,
        validate: (value) => /^(\d{6}|\d{2}-\d{2}-\d{2})$/.test(value.trim()),
      };
    case 'ACCOUNT_NUMBER':
      return {
        label: t`Account number`,
        placeholder: t`12345678`,
        hint: t`Your 8-digit local UK bank account number.`,
        error: t`Enter your 8-digit account number.`,
        validate: (value) => /^\d{8}$/.test(value.trim()),
      };
    default:
      // Unknown identifier type: show the raw key Volt sent and just require a
      // value so the payment can still be forwarded.
      return {
        label: key,
        error: t`This field is required.`,
        validate: (value) => value.trim().length > 0,
      };
  }
};

/** True when every required key holds a value that passes its validation. */
export const identifiersComplete = (
  keys: string[],
  values: Record<string, string>,
  bank?: BankContext,
): boolean =>
  keys.every((key) => getAccountIdentifierField(key, bank).validate(values[key] ?? ''));
